import { useMemo } from 'react';
import { create } from 'qrcode';

/**
 * A source tag for attribution (?ref=), e.g. the influencer's handle: letters, digits, '_' and '-' only, at most 32
 * characters. Anything else is dropped; an empty result is undefined. The join page applies the same rule.
 */
export function cleanRef(raw: string | null | undefined): string | undefined {
  const tag = (raw ?? '').replace(/[^A-Za-z0-9_-]/g, '').slice(0, 32);
  return tag || undefined;
}

/** The join link with ?ref=<tag> added (or replaced), for the QR codes on the stage, host page and builder. */
export function withRef(url: string, ref?: string): string {
  const tag = cleanRef(ref);
  if (!tag) return url;
  try {
    const u = new URL(url);
    u.searchParams.set('ref', tag);
    return u.toString();
  } catch {
    const [path, query = ''] = url.split('?');
    const params = new URLSearchParams(query); params.set('ref', tag);
    return `${path}?${params.toString()}`;
  }
}

/**
 * The link a QR code carries (16 Oct 2026): the join link tagged ?ref=qr-<where>, so a scan reads apart from a typed
 * address or a bio link in the battle's figures. `where` names the screen (stage, host, created); a source tag the screen
 * was given (the presenter link's ?ref=tiktok) takes its place: qr-tiktok. At most 32 characters, like every tag.
 */
export function qrLink(url: string, where: string, tag?: string): string {
  return withRef(url, `qr-${cleanRef(tag) ?? where}`.slice(0, 32));
}

/** The QR module matrix for a text: `size` × `size`, true = dark. */
export function qrMatrix(text: string, level: 'M' | 'Q' | 'H' = 'Q'): { size: number; dark: (x: number, y: number) => boolean } {
  const qr = create(text, { errorCorrectionLevel: level });
  const { size, data } = qr.modules;
  return { size, dark: (x, y) => x >= 0 && y >= 0 && x < size && y < size && Boolean(data[y * size + x]) };
}

const inFinder = (x: number, y: number, n: number) => (x < 7 && y < 7) || (x >= n - 7 && y < 7) || (x < 7 && y >= n - 7);

/** Dark modules as one path of horizontal runs, finder patterns left out (they are drawn as rounded eyes). */
function modulesPath(m: ReturnType<typeof qrMatrix>): string {
  const n = m.size; let d = '';
  for (let y = 0; y < n; y++) {
    let x = 0;
    while (x < n) {
      if (!m.dark(x, y) || inFinder(x, y, n)) { x++; continue; }
      let run = 1; while (x + run < n && m.dark(x + run, y) && !inFinder(x + run, y, n)) run++;
      d += `M${x} ${y}h${run}v1h-${run}z`;
      x += run;
    }
  }
  return d;
}

/**
 * A QR code drawn as crisp SVG: square modules (what scanners read best), rounded finder eyes, a white quiet zone.
 * With `badge`, a small app mark sits in the middle; the code then uses the highest error correction so it still scans.
 */
export function Qr({ value, size = 220, label, className = '', badge = false, ink = 'var(--ink, #0D1B2A)' }: {
  value: string; size?: number; label?: string; className?: string; badge?: boolean; ink?: string;
}) {
  const m = useMemo(() => qrMatrix(value, badge ? 'H' : 'Q'), [value, badge]);
  const path = useMemo(() => modulesPath(m), [m]);
  const n = m.size; const q = 3; // quiet zone in modules (scanners want 4; 3 on a white card with its own padding is plenty)
  const eye = (x: number, y: number) => (
    <g key={`${x}-${y}`}>
      <rect x={x + 0.5} y={y + 0.5} width={6} height={6} rx={1.9} fill="none" stroke={ink} strokeWidth={1} />
      <rect x={x + 2} y={y + 2} width={3} height={3} rx={0.9} fill={ink} />
    </g>
  );
  const b = Math.round(n * 0.22); const bx = (n - b) / 2;
  return (
    <svg viewBox={`${-q} ${-q} ${n + 2 * q} ${n + 2 * q}`} width={size} height={size} role="img" aria-label={label ?? `QR code for ${value}`}
      className={className} shapeRendering="crispEdges" data-modules={n}>
      <rect x={-q} y={-q} width={n + 2 * q} height={n + 2 * q} fill="#FFFFFF" />
      <path d={path} fill={ink} />
      <g shapeRendering="geometricPrecision">
        {eye(0, 0)}{eye(n - 7, 0)}{eye(0, n - 7)}
        {badge && (
          <g>
            <rect x={bx - 0.6} y={bx - 0.6} width={b + 1.2} height={b + 1.2} rx={b * 0.3} fill="#FFFFFF" />
            <rect x={bx} y={bx} width={b} height={b} rx={b * 0.26} fill="var(--brand, #0E9F94)" />
            <path d={`M${bx + b * 0.14} ${bx + b * 0.56} h${b * 0.2} l${b * 0.09} ${-b * 0.22} l${b * 0.12} ${b * 0.42} l${b * 0.1} ${-b * 0.34} l${b * 0.07} ${b * 0.14} h${b * 0.14}`}
              fill="none" stroke="#FFFFFF" strokeWidth={b * 0.09} strokeLinecap="round" strokeLinejoin="round" />
          </g>
        )}
      </g>
    </svg>
  );
}

/**
 * A PNG of the join code for sharing (an Instagram story, a slide): the QR on a white card with the code and link under it.
 * Browser only (canvas); resolves undefined where canvas is unavailable.
 */
export async function qrPng(value: string, opts: { code?: string; caption?: string; px?: number } = {}): Promise<string | undefined> {
  const px = opts.px ?? 1080;
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext?.('2d'); if (!ctx) return undefined;
  const m = qrMatrix(value, 'Q'); const n = m.size;
  const pad = Math.round(px * 0.08); const cell = Math.floor((px - pad * 2) / (n + 6));
  const qrPx = cell * (n + 6); const textH = opts.code ? Math.round(px * 0.3) : Math.round(px * 0.06);
  canvas.width = px; canvas.height = pad + qrPx + textH;
  ctx.fillStyle = '#FFFFFF'; ctx.fillRect(0, 0, canvas.width, canvas.height);
  const ox = Math.round((px - qrPx) / 2) + cell * 3; const oy = pad + cell * 3;
  ctx.fillStyle = '#0D1B2A';
  for (let y = 0; y < n; y++) for (let x = 0; x < n; x++) if (m.dark(x, y)) ctx.fillRect(ox + x * cell, oy + y * cell, cell, cell);
  if (opts.code) {
    ctx.textAlign = 'center'; ctx.fillStyle = '#0D1B2A';
    ctx.font = `800 ${Math.round(px * 0.13)}px 'Bricolage Grotesque', system-ui, sans-serif`;
    ctx.fillText(opts.code.split('').join(' '), px / 2, pad + qrPx + Math.round(px * 0.15));
    if (opts.caption) { ctx.fillStyle = '#34445A'; ctx.font = `500 ${Math.round(px * 0.04)}px 'Instrument Sans', system-ui, sans-serif`; ctx.fillText(opts.caption, px / 2, pad + qrPx + Math.round(px * 0.235)); }
  }
  return canvas.toDataURL('image/png');
}
